import React from "react";
import NullableValue from "Utils/renderField/nullableValue";
import { RenderDollar } from "Utils/renderField/renderReadField";
import {
    RenderKgSecos,
    RenderPorcentaje,
} from "../../../components/CeldasReporte";
import { formatearFecha } from "../../../helpers/formatearFecha";
import { TITULOS_POR_TIPO } from "../helpers/tiposReporteCierres";

// Diferencia de un cierre sin traslados con qué comparar: se muestra un guion,
// igual que en el comparativo del cierre
const KgSecosOpcional = ({ value }) => (
    <NullableValue value={value}>
        <RenderKgSecos value={value} />
    </NullableValue>
);

const PorcentajeOpcional = ({ value }) => (
    <NullableValue value={value}>
        <RenderPorcentaje value={value} />
    </NullableValue>
);

// Sin compras de sólidos en el rango del cierre no hay precio, y sin precio o
// sin diferencia total no hay diferencia en USD
const DolaresOpcional = ({ value }) => (
    <NullableValue value={value}>
        <RenderDollar value={value} />
    </NullableValue>
);

const Mensaje = ({ children }) => (
    <div className="text-center py-5">
        <h3>{children}</h3>
    </div>
);

// Una fila por cierre mensual registrado, más la de totales. Las columnas de
// acumulación no tienen total porque su última fila ya lo es, y el precio es
// de cada cierre
export const ReporteCierresMensualesTable = ({ hook }) => {
    const { reporteDelTipo, tipo, mensajeError } = hook;

    if (mensajeError) {
        return <Mensaje>{mensajeError}</Mensaje>;
    }

    if (!reporteDelTipo) {
        return (
            <Mensaje>
                Seleccione un rango de fechas, un producto padre, y presione
                Filtrar para generar el reporte
            </Mensaje>
        );
    }

    if (!reporteDelTipo.filas.length) {
        return (
            <Mensaje>
                No hay cierres mensuales del producto padre con todos sus
                periodos dentro del rango seleccionado
            </Mensaje>
        );
    }

    const titulos = TITULOS_POR_TIPO[tipo];

    const renderCeldasKg = (fila) => (
        <React.Fragment>
            <td className="text-right text-nowrap">
                <RenderKgSecos value={fila.kg_secos_inicial} />
            </td>
            <td className="text-right text-nowrap">
                <RenderKgSecos value={fila.kg_secos_ingresos} />
            </td>
            <td className="text-right text-nowrap">
                <RenderKgSecos value={fila.kg_secos_skim_no_conforme} />
            </td>
            <td className="text-right text-nowrap">
                <RenderKgSecos value={fila.kg_secos_final} />
            </td>
            <td className="text-right text-nowrap fw-bold">
                <RenderKgSecos value={fila.kg_secos_enviados} />
            </td>

            <td className="text-right text-nowrap cell-border-left">
                <RenderKgSecos value={fila.kg_secos_produccion} />
            </td>

            <td className="text-right text-nowrap cell-border-left">
                <RenderKgSecos value={fila.diferencia_patios} />
            </td>
            <td className="text-right text-nowrap">
                <KgSecosOpcional value={fila.diferencia_acopio} />
            </td>
            <td className="text-right text-nowrap fw-bold">
                <KgSecosOpcional value={fila.diferencia_total} />
            </td>
        </React.Fragment>
    );

    return (
        <div className="new-table-wrapper my-4 sticky-header sticky-first-column">
            <table className="table-striped-v2">
                <thead>
                    <tr>
                        <th className="align-middle cell-border-right">
                            Periodo de cierre
                        </th>

                        <th className="align-middle">Inventario inicial (+)</th>
                        <th className="align-middle">{titulos.ingresos}</th>
                        <th
                            className="align-middle"
                            title="Ingresos de los almacenes de reproceso y de los que requieren bultos de skim"
                        >
                            Skim y producto no conforme (+)
                        </th>
                        <th className="align-middle">Inventario final (-)</th>
                        <th
                            className="align-middle"
                            title="Inventario inicial + ingresos + skim y producto no conforme - inventario final"
                        >
                            {titulos.enviados}
                        </th>

                        <th
                            className="align-middle cell-border-left"
                            title="Recibido en bodega, sin reclasificación, más la miga reportada"
                        >
                            Producción en pacas + miga
                        </th>

                        <th
                            className="align-middle cell-border-left"
                            title="Producción - kg secos enviados"
                        >
                            {titulos.diferenciaPatios}
                        </th>
                        <th
                            className="align-middle"
                            title="Diferencia de los traslados registrada en el cierre mensual"
                        >
                            Diferencia acopio vrs ERSA
                        </th>
                        <th
                            className="align-middle"
                            title="Diferencia en patios + diferencia acopio"
                        >
                            {titulos.diferenciaTotal}
                        </th>
                        <th
                            className="align-middle"
                            title="Suma de la diferencia total desde el primer cierre del rango"
                        >
                            Acumulación de kg
                        </th>
                        <th
                            className="align-middle"
                            title="Diferencia total sobre los kg secos enviados"
                        >
                            {titulos.porcentaje}
                        </th>

                        <th
                            className="align-middle cell-border-left min-w-100"
                            title="Promedio de los precios mensuales en USD, sin IVA, del kg seco de sólidos comprado por los perfiles de finca incluidos"
                        >
                            Precio por kg seco
                        </th>
                        <th
                            className="align-middle"
                            title="Precio por kg seco × diferencia total"
                        >
                            Diferencia en USD
                        </th>
                        <th
                            className="align-middle min-w-120"
                            title="Suma de la diferencia en USD desde el primer cierre del rango"
                        >
                            {titulos.acumuladoUsd}
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {reporteDelTipo.filas.map((fila) => (
                        <tr key={fila.cierre_id}>
                            <td className="text-nowrap cell-border-right">
                                {formatearFecha(fila.fecha_inicial)} -{" "}
                                {formatearFecha(fila.fecha_final)}
                            </td>

                            {renderCeldasKg(fila)}

                            <td className="text-right text-nowrap">
                                <KgSecosOpcional
                                    value={fila.diferencia_acumulada}
                                />
                            </td>
                            <td className="text-right text-nowrap">
                                <PorcentajeOpcional
                                    value={fila.porcentaje_variacion}
                                />
                            </td>

                            <td className="text-right text-nowrap cell-border-left">
                                <DolaresOpcional value={fila.precio_kg_seco} />
                            </td>
                            <td className="text-right text-nowrap">
                                <DolaresOpcional value={fila.diferencia_usd} />
                            </td>
                            <td className="text-right text-nowrap">
                                <DolaresOpcional
                                    value={fila.diferencia_usd_acumulada}
                                />
                            </td>
                        </tr>
                    ))}

                    <tr className="totales-row">
                        <td className="text-nowrap fw-bold cell-border-right">
                            Totales
                        </td>

                        {renderCeldasKg(reporteDelTipo.totales)}

                        <td />
                        <td className="text-right text-nowrap">
                            <PorcentajeOpcional
                                value={
                                    reporteDelTipo.totales.porcentaje_variacion
                                }
                            />
                        </td>

                        <td className="cell-border-left" />
                        <td className="text-right text-nowrap">
                            <DolaresOpcional
                                value={reporteDelTipo.totales.diferencia_usd}
                            />
                        </td>
                        <td />
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
